import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Books, FilePdf, PencilLine, GraduationCap, CircleNotch, Plus, PencilSimple, Trash, ArrowLeft, CheckCircle, X, ArrowSquareOut, LinkSimple, UploadSimple, FolderSimple, FileText, ClipboardText, Books as BooksIcon } from "@phosphor-icons/react";

const TABS = [
    { key: "questions", label: "Questions", icon: Books },
    { key: "documents", label: "Documents", icon: FolderSimple },
    { key: "pyq", label: "PYQ Papers", icon: FilePdf },
    { key: "mains", label: "Mains Prompts", icon: PencilLine },
    { key: "study", label: "Study Material", icon: GraduationCap },
];

const BOARD_LABEL = { kpsc: "KPSC", ksp: "KSP", kea: "KEA" };

// ---------- Sub-panels ----------

const QuestionsPanel = ({ examSlug, board, onCountChange }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const emptyForm = () => ({ board, exam_id: examSlug, subject: "General Studies", content: "", options: ["", "", "", ""], correct_index: 0, explanation: "", from_pyq: false, year: null, difficulty: "medium" });
    const [form, setForm] = useState(emptyForm());
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/questions", { params: { exam_id: examSlug } }); setItems(data.items); onCountChange?.("questions", data.items.length); }
        catch (e) { toast.error("Failed to load"); } finally { setLoading(false); }
    }, [examSlug, onCountChange]);
    useEffect(() => { load(); setForm(emptyForm()); }, [examSlug, load]);

    const save = async () => {
        if (!form.content.trim() || form.options.some((o) => !o.trim())) { toast.error("Question and all options required"); return; }
        try {
            if (editing) await api.put(`/admin/questions/${editing}`, form);
            else await api.post("/admin/questions", form);
            toast.success(editing ? "Updated" : "Created");
            setEditing(null); setForm(emptyForm()); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try { await api.delete(`/admin/questions/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    };
    const openEdit = (q) => { setEditing(q.question_id); setForm({ ...emptyForm(), ...q, options: q.options?.length === 4 ? q.options : ["", "", "", ""] }); };

    return (
        <div className="space-y-4">
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-sm">{editing ? "Edit question" : "New question for this exam"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel</button>}
                </div>
                <textarea data-testid="rq-content" placeholder="Question text" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <div className="grid md:grid-cols-2 gap-2">
                    {form.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <button onClick={() => setForm({ ...form, correct_index: i })} className={`w-7 h-7 rounded-full border-2 grid place-items-center text-[11px] font-bold ${form.correct_index === i ? "bg-emerald-100 border-emerald-500 text-emerald-800" : "border-slate-300 text-slate-500"}`}>
                                {String.fromCharCode(65 + i)}
                            </button>
                            <input data-testid={`rq-opt-${i}`} placeholder={`Option ${String.fromCharCode(65 + i)}`} value={opt} onChange={(e) => { const opts = [...form.options]; opts[i] = e.target.value; setForm({ ...form, options: opts }); }} className="flex-1 px-3 py-1.5 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        </div>
                    ))}
                </div>
                <textarea placeholder="Explanation (optional)" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <div className="flex items-center gap-3 text-xs">
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-2 py-1 border-2 border-[#BFD3FF] rounded-md" />
                    <label className="inline-flex items-center gap-1"><input type="checkbox" checked={form.from_pyq} onChange={(e) => setForm({ ...form, from_pyq: e.target.checked })} /> PYQ</label>
                    <input type="number" placeholder="Year" value={form.year || ""} onChange={(e) => setForm({ ...form, year: e.target.value ? parseInt(e.target.value) : null })} className="w-20 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" />
                    <button data-testid="rq-save" onClick={save} className="ml-auto btn-primary !py-1.5 !px-3"><CheckCircle size={14} weight="fill" /> {editing ? "Update" : "Create"}</button>
                </div>
            </div>
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-500">{loading ? "Loading…" : `${items.length} question(s)`}</div>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {items.map((q) => (
                                <tr key={q.question_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-2 text-xs text-slate-700 max-w-md truncate">{q.content || q.question}</td>
                                    <td className="px-2 py-2 text-[10px] text-slate-400">{q.subject}</td>
                                    <td className="px-2 py-2 text-right w-20">
                                        <button onClick={() => openEdit(q)} className="p-1 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={13} /></button>
                                        <button onClick={() => remove(q.question_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash size={13} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={3} className="text-center py-8 text-slate-400 text-xs">No questions for this exam yet.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const PYQPanel = ({ examSlug, onCountChange }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/pyq-papers", { params: { exam_id: examSlug } }); setItems(data); onCountChange?.(); }
        catch { toast.error("Failed to load"); } finally { setLoading(false); }
    }, [examSlug, onCountChange]);
    useEffect(() => { load(); }, [load]);

    const remove = async (id) => {
        if (!window.confirm("Soft-delete this paper?")) return;
        try { await api.delete(`/admin/pyq-papers/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center">
                <Link to={`/admin/pyq/upload?exam_id=${examSlug}`} data-testid="rp-upload" className="ml-auto btn-primary !py-1.5 !px-3 text-xs"><UploadSimple size={14} weight="bold" /> Upload paper for this exam</Link>
            </div>
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-500">{loading ? "Loading…" : `${items.length} paper(s)`}</div>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.paper_id} className={`border-t border-slate-100 hover:bg-slate-50 ${p.is_deleted ? "opacity-50" : ""}`}>
                                    <td className="px-4 py-2 text-xs">{p.year} · <span className="capitalize">{p.stage}</span> · {p.paper_type === "answer_key" ? <span className="text-emerald-700 font-bold">Key</span> : "Paper"}</td>
                                    <td className="px-2 py-2 text-xs max-w-xs truncate text-slate-600">{p.title}</td>
                                    <td className="px-2 py-2 text-right w-20">
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pyq-papers/${p.paper_id}/download`} target="_blank" rel="noreferrer" className="inline-block p-1 text-slate-500 hover:text-[#1E3A8A]"><ArrowSquareOut size={13} /></a>
                                        <button onClick={() => remove(p.paper_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash size={13} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={3} className="text-center py-8 text-slate-400 text-xs">No PYQ papers for this exam yet. Upload one above.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const MainsPanel = ({ examSlug, board, onCountChange }) => {
    const [items, setItems] = useState([]);
    const emptyForm = () => ({ board, exam_id: examSlug, subject: "", topic: "", prompt: "", marks: 15, difficulty: "medium", source_year: null });
    const [form, setForm] = useState(emptyForm());
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/mains-prompts", { params: { exam_id: examSlug } }); setItems(data.items); onCountChange?.(); }
        catch { toast.error("Failed to load"); } finally { setLoading(false); }
    }, [examSlug, onCountChange]);
    useEffect(() => { load(); setForm(emptyForm()); }, [examSlug, load]);

    const save = async () => {
        if (!form.prompt.trim()) { toast.error("Prompt required"); return; }
        try {
            if (editing) await api.put(`/admin/mains-prompts/${editing}`, form);
            else await api.post("/admin/mains-prompts", form);
            toast.success(editing ? "Updated" : "Created"); setEditing(null); setForm(emptyForm()); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete this prompt?")) return;
        try { await api.delete(`/admin/mains-prompts/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
    };
    const openEdit = (p) => { setEditing(p.prompt_id); setForm({ ...emptyForm(), ...p }); };

    return (
        <div className="space-y-4">
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-sm">{editing ? "Edit prompt" : "New mains prompt for this exam"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel</button>}
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                </div>
                <textarea data-testid="rm-prompt" placeholder="Prompt text" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={3} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <div className="flex items-center gap-2 text-xs">
                    <label>Marks <input type="number" value={form.marks} onChange={(e) => setForm({ ...form, marks: parseInt(e.target.value || "0") })} className="w-16 ml-1 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" /></label>
                    <label>Year <input type="number" value={form.source_year || ""} onChange={(e) => setForm({ ...form, source_year: e.target.value ? parseInt(e.target.value) : null })} className="w-20 ml-1 px-2 py-1 border-2 border-[#BFD3FF] rounded-md" /></label>
                    <button data-testid="rm-save" onClick={save} className="ml-auto btn-primary !py-1.5 !px-3"><CheckCircle size={14} weight="fill" /> {editing ? "Update" : "Create"}</button>
                </div>
            </div>
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-500">{loading ? "Loading…" : `${items.length} prompt(s)`}</div>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.prompt_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-2 text-xs max-w-md truncate">{p.prompt}</td>
                                    <td className="px-2 py-2 text-[10px] text-slate-400">{[p.subject, p.topic].filter(Boolean).join(" · ")}</td>
                                    <td className="px-2 py-2 text-right w-20">
                                        <button onClick={() => openEdit(p)} className="p-1 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={13} /></button>
                                        <button onClick={() => remove(p.prompt_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash size={13} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={3} className="text-center py-8 text-slate-400 text-xs">No mains prompts for this exam yet.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const StudyPanel = ({ examSlug, onCountChange }) => {
    const [items, setItems] = useState([]);
    const emptyForm = () => ({ title: "", category: "Study Material", exam_id: examSlug, subject: "", summary: "", url: "" });
    const [form, setForm] = useState(emptyForm());
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/study-materials", { params: { exam_id: examSlug } }); setItems(data.items); onCountChange?.(); }
        catch { toast.error("Failed to load"); } finally { setLoading(false); }
    }, [examSlug, onCountChange]);
    useEffect(() => { load(); setForm(emptyForm()); }, [examSlug, load]);

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title required"); return; }
        try {
            if (editing) await api.put(`/admin/study-materials/${editing}`, form);
            else await api.post("/admin/study-materials", form);
            toast.success(editing ? "Updated" : "Created"); setEditing(null); setForm(emptyForm()); load();
        } catch { toast.error("Save failed"); }
    };
    const remove = async (id) => { if (!window.confirm("Delete this material?")) return; try { await api.delete(`/admin/study-materials/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };
    const openEdit = (m) => { setEditing(m.material_id); setForm({ ...emptyForm(), ...m }); };

    return (
        <div className="space-y-4">
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-sm">{editing ? "Edit material" : "New study material for this exam"}</h3>
                    {editing && <button onClick={() => { setEditing(null); setForm(emptyForm()); }} className="text-xs text-slate-500 flex items-center gap-1"><X size={12} /> Cancel</button>}
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                    <input placeholder="Title" data-testid="rs-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <input placeholder="URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm md:col-span-2" />
                </div>
                <textarea placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} className="w-full px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" />
                <button data-testid="rs-save" onClick={save} className="ml-auto btn-primary !py-1.5 !px-3"><CheckCircle size={14} weight="fill" /> {editing ? "Update" : "Create"}</button>
            </div>
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-500">{loading ? "Loading…" : `${items.length} material(s)`}</div>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {items.map((m) => (
                                <tr key={m.material_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-2 text-xs font-medium">{m.title}</td>
                                    <td className="px-2 py-2 text-[10px] text-slate-400">{m.subject || "—"}</td>
                                    <td className="px-2 py-2 text-right w-24">
                                        {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="p-1 inline-block text-slate-500 hover:text-[#1E3A8A]"><LinkSimple size={13} /></a>}
                                        <button onClick={() => openEdit(m)} className="p-1 text-slate-500 hover:text-[#1E3A8A]"><PencilSimple size={13} /></button>
                                        <button onClick={() => remove(m.material_id)} className="p-1 text-slate-500 hover:text-red-600"><Trash size={13} /></button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && !loading && (<tr><td colSpan={3} className="text-center py-8 text-slate-400 text-xs">No study material for this exam yet.</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ---------- Documents Panel (Syllabus / PYQ / Study Material file uploads) ----------
const DOC_TYPES = [
    { key: "syllabus",       label: "Syllabus",       icon: ClipboardText, desc: "Official syllabus PDFs for this exam" },
    { key: "pyq",            label: "PYQ",            icon: FileText,       desc: "Previous year question paper PDFs" },
    { key: "study_material", label: "Study Material", icon: BooksIcon,      desc: "Reference notes, handouts and study PDFs" },
];

const fmtBytes = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

const DocumentsPanel = ({ examSlug, onCountChange }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState("syllabus");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/documents", { params: { exam_id: examSlug } }); setItems(data.items); onCountChange?.(); }
        catch { toast.error("Failed to load documents"); }
        finally { setLoading(false); }
    }, [examSlug, onCountChange]);
    useEffect(() => { load(); }, [load]);

    const upload = async () => {
        if (!title.trim()) { toast.error("Title required"); return; }
        if (!file) { toast.error("Select a file to upload"); return; }
        const fd = new FormData();
        fd.append("exam_id", examSlug);
        fd.append("doc_type", activeType);
        fd.append("title", title.trim());
        fd.append("description", description.trim());
        fd.append("file", file);
        setUploading(true);
        try {
            await api.post("/admin/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setTitle(""); setDescription(""); setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        try { await api.delete(`/admin/documents/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    const filtered = items.filter((d) => d.doc_type === activeType);
    const countsByType = DOC_TYPES.reduce((acc, t) => { acc[t.key] = items.filter((d) => d.doc_type === t.key).length; return acc; }, {});
    const activeMeta = DOC_TYPES.find((t) => t.key === activeType);

    return (
        <div className="space-y-4">
            {/* Doc-type sub-tabs */}
            <div className="flex flex-wrap gap-2">
                {DOC_TYPES.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`doc-type-${t.key}`}
                        onClick={() => setActiveType(t.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors ${
                            activeType === t.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                        }`}
                    >
                        <t.icon size={13} weight="bold" /> {t.label}
                        <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded-full text-[10px] ${activeType === t.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                            {countsByType[t.key]}
                        </span>
                    </button>
                ))}
            </div>

            {/* Upload widget */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-3">
                <div>
                    <h3 className="font-heading font-bold text-sm">Upload {activeMeta.label} document</h3>
                    <p className="text-[11px] text-slate-500">{activeMeta.desc}. Supported: PDF, DOC, DOCX, PNG, JPG, WEBP · Max 25 MB.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <input
                        data-testid="doc-title"
                        placeholder="Title (e.g. KAS 2024 Prelims Syllabus)"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm"
                    />
                    <input
                        placeholder="Short description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm"
                    />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={fileInputRef}
                        data-testid="doc-file"
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-2 file:border-[#BFD3FF] file:bg-white file:text-[#1E3A8A] file:font-semibold file:text-xs hover:file:border-[#1E3A8A]"
                    />
                    {file && <span className="text-[11px] text-slate-500">{file.name} · {fmtBytes(file.size)}</span>}
                    <button
                        data-testid="doc-upload-btn"
                        onClick={upload}
                        disabled={uploading || !file || !title.trim()}
                        className="ml-auto btn-primary !py-2 !px-4 disabled:opacity-50"
                    >
                        {uploading ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} weight="bold" />}
                        {uploading ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>

            {/* Document list */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    {loading ? "Loading…" : `${filtered.length} ${activeMeta.label} document(s)`}
                </div>
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {filtered.map((d) => (
                                <tr key={d.doc_id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="px-4 py-2 text-xs">
                                        <div className="font-medium text-slate-800">{d.title}</div>
                                        {d.description && <div className="text-[11px] text-slate-500 line-clamp-1">{d.description}</div>}
                                        <div className="text-[10px] text-slate-400 mt-0.5">{d.filename} · {fmtBytes(d.size_bytes || 0)} · {fmtDate(d.uploaded_at)}</div>
                                    </td>
                                    <td className="px-2 py-2 text-right w-20">
                                        <a
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.doc_id}/download`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`doc-view-${d.doc_id}`}
                                            className="p-1.5 inline-block text-slate-500 hover:text-[#1E3A8A]"
                                            title="View / download"
                                        >
                                            <ArrowSquareOut size={13} />
                                        </a>
                                        <button
                                            onClick={() => remove(d.doc_id)}
                                            data-testid={`doc-del-${d.doc_id}`}
                                            className="p-1.5 text-slate-500 hover:text-red-600"
                                            title="Delete"
                                        >
                                            <Trash size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && !loading && (
                                <tr><td colSpan={2} className="text-center py-8 text-slate-400 text-xs">No {activeMeta.label.toLowerCase()} documents yet — upload one above.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ---------- Page ----------
export default function AdminRepositoryDetail() {
    const { slug } = useParams();
    const [repo, setRepo] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("questions");

    const load = useCallback(async () => {
        try { const { data } = await api.get(`/admin/repositories/${slug}`); setRepo(data); }
        catch (e) { setError(e.response?.data?.detail || "Failed to load"); }
    }, [slug]);
    useEffect(() => { load(); }, [load]);

    if (error) return <div className="text-red-700 text-sm">{error}</div>;
    if (!repo) return <div className="text-slate-500 flex items-center gap-2 text-sm"><CircleNotch size={16} className="animate-spin" /> Loading repository…</div>;

    return (
        <div className="space-y-6">
            <Link to="/admin/repositories" data-testid="repo-back" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E3A8A] hover:underline">
                <ArrowLeft size={14} weight="bold" /> All repositories
            </Link>

            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{BOARD_LABEL[repo.board] || repo.board} · Repository</div>
                        <h1 className="font-heading font-black text-3xl mt-1">{repo.name}</h1>
                        <div className="text-sm text-slate-600 mt-1">{repo.full_name}</div>
                        <p className="text-xs text-slate-500 mt-2 max-w-2xl">{repo.description}</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {[
                            { k: "questions", label: "Questions", v: repo.counts?.questions || 0, icon: Books },
                            { k: "documents", label: "Docs", v: repo.counts?.documents || 0, icon: FolderSimple },
                            { k: "pyq_papers", label: "PYQ", v: repo.counts?.pyq_papers || 0, icon: FilePdf },
                            { k: "mains_prompts", label: "Mains", v: repo.counts?.mains_prompts || 0, icon: PencilLine },
                            { k: "study_materials", label: "Study", v: repo.counts?.study_materials || 0, icon: GraduationCap },
                        ].map((s) => (
                            <div key={s.k} className="border-2 border-[#BFD3FF] rounded-xl px-3 py-2 min-w-[90px]">
                                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-bold"><s.icon size={11} weight="duotone" /> {s.label}</div>
                                <div className="font-heading font-black text-2xl text-slate-900 tabular-nums">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`repo-tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-colors ${
                            tab === t.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                        }`}
                    >
                        <t.icon size={13} weight="bold" /> {t.label}
                    </button>
                ))}
            </div>

            <div>
                {tab === "questions" && <QuestionsPanel examSlug={slug} board={repo.board} onCountChange={load} />}
                {tab === "documents" && <DocumentsPanel examSlug={slug} onCountChange={load} />}
                {tab === "pyq" && <PYQPanel examSlug={slug} onCountChange={load} />}
                {tab === "mains" && <MainsPanel examSlug={slug} board={repo.board} onCountChange={load} />}
                {tab === "study" && <StudyPanel examSlug={slug} onCountChange={load} />}
            </div>
        </div>
    );
}
